import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser, setStoredUser } from '../utils/userStorage';

function ProfileSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    role: 'user',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    marketing_email_opt_out: false
  });

  useEffect(() => {
    // Get user data from storage
    const storedUser = getStoredUser();
    if (!storedUser) {
      navigate('/login');
      return;
    }

    setUser(storedUser);
    setFormData({
      name: storedUser.name || '',
      email: storedUser.email || '',
      company_name: storedUser.company_name || '',
      phone: storedUser.phone || '',
      role: storedUser.role || 'user',
      address: {
        street1: storedUser.address?.street1 || '',
        street2: storedUser.address?.street2 || '',
        city: storedUser.address?.city || '',
        state: storedUser.address?.state || '',
        postal_code: storedUser.address?.postal_code || '',
        country: storedUser.address?.country || ''
      },
      marketing_email_opt_out: storedUser.marketing_email_opt_out || false
    });
  }, [navigate]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 5;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name || formData.name.trim().length < 4) {
      setError('Please enter a valid name (minimum 4 characters)');
      setLoading(false);
      return;
    }

    if (!formData.phone || !validatePhone(formData.phone)) {
      setError('Please enter a valid phone number (at least 5 digits)');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE}/api/users/${user.id}`,
        {
          name: formData.name,
          company_name: formData.company_name,
          phone: formData.phone,
          role: formData.role,
          address: formData.address,
          marketing_email_opt_out: formData.marketing_email_opt_out
        }
      );

      // Update stored user data
      const updatedUser = { ...user, ...response.data };
      setStoredUser(updatedUser);
      setUser(updatedUser);

      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile: ' + (err?.response?.data?.error || err.message));
    }
    setLoading(false);
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#1f1f1f', fontWeight: 700 }}>
        Profile Settings
      </Typography>

      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1f1f1f', fontWeight: 600 }}>
              Contact Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  disabled
                  sx={{ mb: 2 }}
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="Enter your company name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="developer">Developer</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 3, mt: 4, color: '#1f1f1f', fontWeight: 600 }}>
              Address Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 1"
                  value={formData.address.street1}
                  onChange={(e) => handleInputChange('address.street1', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 2 (Optional)"
                  value={formData.address.street2}
                  onChange={(e) => handleInputChange('address.street2', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.address.postal_code}
                  onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.address.country}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, color: '#1f1f1f', fontWeight: 600 }}>
              Communication Preferences
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.marketing_email_opt_out}
                    onChange={(e) => handleInputChange('marketing_email_opt_out', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#000',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#000',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.marketing_email_opt_out ? 'Opted Out' : 'Opted In'} - Marketing Emails
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                      {formData.marketing_email_opt_out
                        ? 'You will not receive marketing emails from us. You can opt back in at any time.'
                        : 'Receive product updates, tips, and promotional content via email. You can opt out at any time.'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/app/dashboard')}
                sx={{ px: 4, py: 1.5 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#1f1f1f', 
                  color: '#fff',
                  '&:hover': { bgcolor: '#1f1f1f' }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default ProfileSettings; 